import { AppletHostError } from '../applet-host-error';
import { AppletHostErrorCode } from '../applet-host-error-code';
import { DEFAULT_NAMESPACE } from '../default-namespace';
import { logger } from '../logger';
import { version } from '../../package.json';
import { wrapDateRangeRepositoryForDateRangeV3Upgrade } from './wrap-date-range-repository-for-date-range-v3-upgrade';

export const VERSION_ZERO_APPLET_HOST_NAMESPACE = 'MeltwaterAppletHostV0';
export const VERSION_ONE_APPLET_HOST_NAMESPACE = 'MeltwaterAppletHostV1';

/**
 * @private
 * @deprecated until v3
 * @return {undefined}
 */
export function installAppletHostBackwardsCompatibilityV0({ appletHostInstallOptions, currentAppletHost }) {
    if (globalThis[VERSION_ZERO_APPLET_HOST_NAMESPACE]) {
        logger.warn(`%c
        ********************************************************************************
        ********************************************************************************
        ********************************************************************************`, 'font-size: 24px; background: #222; color: #ff0000');
        logger.warn(`%c
            Version 0 of the AppletHost will no longer be supported as of Version 3.
                        Please take any required steps to upgrade NOW!

             Backwards compatibility for Version 0 requires Version 1 to be available.
                You now have 3 different versions of the AppletHost running!!!!!!`, 'font-size: 24px; background: #222; color: #00FF00');
        logger.warn(`%c
        ********************************************************************************
        ********************************************************************************
        ********************************************************************************`, 'font-size: 24px; background: #222; color: #ff0000');

        if (!globalThis[VERSION_ONE_APPLET_HOST_NAMESPACE]) {
            const wrappedAppletHostInstallOptions = wrapDateRangeRepositoryForDateRangeV3Upgrade({ appletHostInstallOptions });
            installAppletHostBackwardsCompatibilityForNamespaces({ appletHostInstallOptions: wrappedAppletHostInstallOptions, currentAppletHost, previousAppletHostWindowNamespace: VERSION_ZERO_APPLET_HOST_NAMESPACE });
        }
    }
}

/**
 * @private
 * @deprecated until v4
 * @return {undefined}
 */
export function installAppletHostBackwardsCompatibilityV1({ appletHostInstallOptions, currentAppletHost }) {
    const wrappedAppletHostInstallOptions = wrapDateRangeRepositoryForDateRangeV3Upgrade({ appletHostInstallOptions });
    installAppletHostBackwardsCompatibilityForNamespaces({ appletHostInstallOptions: wrappedAppletHostInstallOptions, currentAppletHost, previousAppletHostWindowNamespace: VERSION_ONE_APPLET_HOST_NAMESPACE });
}

function installAppletHostBackwardsCompatibilityForNamespaces({ appletHostInstallOptions, currentAppletHost, previousAppletHostWindowNamespace }) {
    if (globalThis[previousAppletHostWindowNamespace]) {
        const PREVIOUS_DEFAULT_NAMESPACE = globalThis[previousAppletHostWindowNamespace].DEFAULT_NAMESPACE;
        const PreviousAppletHost = globalThis[previousAppletHostWindowNamespace].AppletHost;
        let previousAppletHost;

        try {
            previousAppletHost = PreviousAppletHost.install(appletHostInstallOptions);
        } catch (err) {
            // AppletHost v0 just throws a new Error instead of an AppletHostError when installing duplicate hosts
            // so match error message to regex for now if testing for duplicate v0 host
            if (err.message.match(/cannot install multiple times/) !== null) {
                const errorMsg = `Error installing the AppletHost for ${previousAppletHostWindowNamespace} in AppletHost v${version}.\n\t` +
                    `AppletHost v${version}'s shim is currently trying to install the AppletHost for ${previousAppletHostWindowNamespace}, but there may already be one installed by ${previousAppletHostWindowNamespace}.\n\t` +
                    `Please allow AppletHost v${version} to handle the installation of the AppletHost for ${previousAppletHostWindowNamespace}.\n\t` +
                    'Please reach out to team Legion with any questions, and we\'d be more than happy to help!';

                logger.error(errorMsg);
                throw new AppletHostError({
                    message: err.message,
                    code: AppletHostErrorCode.DUPLICATE_HOST_OF_PREVIOUS_VERSION
                });
            }
            throw err;
        }

        const namespacesInPreviousAppletHost = [
            PREVIOUS_DEFAULT_NAMESPACE,
            ...Object.keys(appletHostInstallOptions.namespaces)
        ];
        const namespacesInCurrentAppletHost = [
            DEFAULT_NAMESPACE,
            ...Object.keys(appletHostInstallOptions.namespaces)
        ];

        const namespaceCallbackStateMap = {};

        namespacesInCurrentAppletHost.forEach((namespace) => {
            namespaceCallbackStateMap[namespace] = {
                fromCurrentVersionSettingChangeEventRegistrar: false,
                fromPreviousVersionSettingChangeEventRegistrar: false
            };
        });

        namespacesInCurrentAppletHost.forEach((namespace) => {
            currentAppletHost.settingsChangeEventRegistrar.register({
                callback: async ({ namespace }) => {
                    const namespaceState = namespaceCallbackStateMap[namespace];
                    const namespaceToFire = namespace === DEFAULT_NAMESPACE ? PREVIOUS_DEFAULT_NAMESPACE : namespace;
                    if (
                        !namespaceState.fromCurrentVersionSettingChangeEventRegistrar &&
                        !namespaceState.fromPreviousVersionSettingChangeEventRegistrar
                    ) {
                        namespaceState.fromCurrentVersionSettingChangeEventRegistrar = true;
                        await previousAppletHost.settingsChangeEventRegistrar.fire({
                            namespace: namespaceToFire
                        });
                    } else if (namespaceState.fromPreviousVersionSettingChangeEventRegistrar) {
                        namespaceState.fromPreviousVersionSettingChangeEventRegistrar = false;
                    }
                },
                namespace
            });
        });

        namespacesInPreviousAppletHost.forEach((namespace) => {
            previousAppletHost.settingsChangeEventRegistrar.register({
                callback: async ({ namespace }) => {
                    const namespaceToFire = namespace === PREVIOUS_DEFAULT_NAMESPACE ? DEFAULT_NAMESPACE : namespace;
                    const namespaceState = namespaceCallbackStateMap[namespaceToFire];
                    if (
                        !namespaceState.fromCurrentVersionSettingChangeEventRegistrar &&
                        !namespaceState.fromPreviousVersionSettingChangeEventRegistrar
                    ) {
                        namespaceState.fromPreviousVersionSettingChangeEventRegistrar = true;
                        await currentAppletHost.settingsChangeEventRegistrar.fire({
                            namespace: namespaceToFire
                        });
                    } else if (namespaceState.fromCurrentVersionSettingChangeEventRegistrar) {
                        namespaceState.fromCurrentVersionSettingChangeEventRegistrar = false;
                    }
                },
                namespace
            });
        });
    }
}
